import { ExternalLink, Eye, EyeOff, Info, TriangleAlert } from 'lucide-react'
import { equals } from 'ramda'
import React, { useEffect, useRef, useState } from 'react'
import { flushSync } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router'
import { ToastContainer } from 'react-toastify'
import { setAppSessionJwts } from '~/atoms/AuthenticationState'
import { Alert, FontStyle, Input, Text } from '~/components/core'
import {
  MangoButton,
  MangoButtonVariant,
} from '~/components/shared/MangoButton'
import { useAnalytics } from '~/hooks/useAnalytics'
import { usePartnerLogo } from '~/hooks/useCurrentPartner'
import { useMouseEvents } from '~/hooks/useMouseEvents'
import { ApplicationRoutes } from '~/hooks/useRouting'
import { LoginEnvironmentToggle } from '~/pages/Login/LoginEnvironmentToggle'
import { useLoginWithPassword } from '~/queries/loginQueries'
import {
  LoginByEmailErrorResponse,
  LoginByEmailResponse,
  LoginSecurityMessageResponse,
} from '~/types'
import {
  AnalyticsNamespace,
  AuthenticationAnalyticsEvents,
} from '~/types/Analytics'
import { Size } from '~/types/ThemeTypes'
import { detectAutofillAsync } from '~/utils/browser'
import { validateEmail } from '~/utils/formatter'
import { openInNewTab } from '~/utils/functions'

import {
  ContentWrapper,
  ErrorWrapper,
  ForgotButtonWrapper,
  ForgotPasswordLink,
  InputWrapper,
  LoginButtonWrapper,
  LoginMessageActionWrapper,
  LoginMessageWrapper,
  LogoImage,
  PassButtonWrapper,
  PasswordVisibilityButton,
} from './Login.styles'

const LOGIN_ERROR_ID = 'login-error'
const USERNAME_ERROR_ID = 'username-error'
const PASSWORD_ERROR_ID = 'password-error'

const describedBy = (...ids: Array<string | false>) =>
  ids.filter(Boolean).join(' ') || undefined

interface LoginPromptProps {
  onLoginSuccess?: () => void
}

const isLoginSecurityMessage = (
  error: LoginByEmailErrorResponse | null | undefined,
): error is LoginSecurityMessageResponse => {
  return (
    (error?.code === 'password_update_support_form' ||
      error?.code === 'password_reset_email_sent' ||
      error?.code === 'password_update_admin_notified' ||
      error?.code === 'password_reset_required') &&
    typeof error.message === 'string' &&
    error.message.length > 0
  )
}

const LoginSecurityMessage = ({
  message,
  onBackToLogin,
}: {
  message: LoginSecurityMessageResponse
  onBackToLogin: () => void
}) => {
  const { t } = useTranslation()
  const actionUrl = message.action_url ?? message.support_form_url

  return (
    <LoginMessageWrapper role="status" aria-live="polite">
      <Alert
        type={message.level === 'info' ? 'primary' : 'warning'}
        icon={
          message.level === 'info' ? (
            <Info size={24} color="var(--chakra-colors-brand-solid)" />
          ) : (
            <TriangleAlert size={24} color="var(--chakra-colors-red-solid)" />
          )
        }
      >
        {message.message}
      </Alert>
      <LoginMessageActionWrapper>
        {actionUrl && (
          <MangoButton
            onClick={() => openInNewTab(actionUrl)}
            rightIcon={ExternalLink}
            size={Size.md}
          >
            {message.action_label ??
              t('loginPrompt.openSupportForm', 'Open support form')}
          </MangoButton>
        )}
        <MangoButton
          onClick={onBackToLogin}
          variant={MangoButtonVariant.Default_200}
          size={Size.md}
        >
          {t('loginPrompt.backToLogin', 'Back to login')}
        </MangoButton>
      </LoginMessageActionWrapper>
    </LoginMessageWrapper>
  )
}

export const LoginPrompt = ({ onLoginSuccess }: LoginPromptProps) => {
  const { t } = useTranslation()
  const { track } = useAnalytics(AnalyticsNamespace.AUTHENTICATION)
  const [username, setUsername] = useState('')
  const [errUsername, setErrUsername] = useState('')
  const [autoFilled, setAutoFilled] = useState(false)
  const [password, setPassword] = useState('')
  const [errPassword, setErrPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [isSubmitted, setIsSubmitted] = useState(false)

  const usernameInput = useRef<HTMLInputElement>(null)
  const passwordInput = useRef<HTMLInputElement>(null)
  const partnerLogo = usePartnerLogo()

  const getAutoFillStatus = async () => {
    if (!usernameInput.current || !passwordInput.current) return

    const usernameAutofillPromise = detectAutofillAsync(
      usernameInput.current,
      300,
    )
    const passwordAutofillPromise = detectAutofillAsync(
      passwordInput.current,
      300,
    )

    const results = await Promise.all([
      usernameAutofillPromise,
      passwordAutofillPromise,
    ])
    if (!results.every((r) => r === true)) {
      return
    }

    setAutoFilled(true)
  }

  const isMouseDown = useMouseEvents()

  //on mount, attempt to read autofill data from browser without user interaction
  useEffect(() => {
    void getAutoFillStatus()
  }, [])

  useEffect(() => {
    if (isMouseDown) {
      if (autoFilled) {
        //attempt to read the input values
        const username = usernameInput.current?.value
        const password = passwordInput.current?.value
        setUsername(username ?? '')
        setPassword(password ?? '')
        setAutoFilled(false)
      }
    }
  }, [isMouseDown])

  const onLoginSuccessInternal = (data: LoginByEmailResponse) => {
    setAppSessionJwts({
      access_token: data.access,
      refresh_token: data.refresh,
    })
    onLoginSuccess?.()
  }

  const {
    refetch: loginRefetch,
    clearLoginError,
    error: loginError,
    isError,
    isFetching,
  } = useLoginWithPassword(onLoginSuccessInternal)
  useEffect(() => {
    if (!isFetching) {
      setIsSubmitted(false)
    }
  }, [isFetching])

  const loginSecurityMessage = isLoginSecurityMessage(loginError)
    ? loginError
    : undefined

  const login = () => {
    loginRefetch(username, password)
    setIsSubmitted(true)
  }

  const getUsernameError = () => {
    if (!username) return t('loginPrompt.enterUsername')
    if (password && !validateEmail(username))
      return t('loginPrompt.invalidEmail')
    return ''
  }

  const handleLogin = () => {
    const usernameError = getUsernameError()
    const passwordError = password ? '' : t('loginPrompt.enterPassword')

    if (!usernameError && !passwordError) {
      login()
      return
    }

    // Commit the errors before moving focus so the focused field is
    // announced together with its aria-describedby error text.
    flushSync(() => {
      if (usernameError) setErrUsername(usernameError)
      if (passwordError) setErrPassword(passwordError)
    })
    const firstInvalidInput = usernameError ? usernameInput : passwordInput
    firstInvalidInput.current?.focus()
  }

  const handleForgotPassword = () => {
    track(AuthenticationAnalyticsEvents.ForgotPasswordClicked)
  }

  const handleBackToLogin = () => {
    clearLoginError()
    setPassword('')
    setErrUsername('')
    setErrPassword('')
    setIsSubmitted(false)
    setShowPassword(false)
    setAutoFilled(false)
  }

  const onChangeUsername = (changedUsername: string) => {
    setUsername(changedUsername)
    setIsSubmitted(false)
    setErrUsername('')
  }

  const onChangePassword = (changedPassword: string) => {
    setPassword(changedPassword)
    setIsSubmitted(false)
    setErrPassword('')
  }

  const togglePasswordObfuscate = () => {
    setShowPassword(!showPassword)
  }

  const handleKeydownEvent =
    (el: 'username' | 'password') => (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        if (equals(el, 'username')) {
          passwordInput.current?.focus()
        }
        if (equals(el, 'password')) {
          handleLogin()
        }
      }
    }

  const disabled =
    !autoFilled && (username === '' || password === '' || isSubmitted)
  const isUsernameInvalid = isError || errUsername !== ''
  const isPasswordInvalid = isError || errPassword !== ''

  return (
    <>
      <ToastContainer autoClose={2000} pauseOnFocusLoss={false} />
      <ContentWrapper>
        <LogoImage alt="logo" src={partnerLogo} />
        {loginSecurityMessage ? (
          <LoginSecurityMessage
            message={loginSecurityMessage}
            onBackToLogin={handleBackToLogin}
          />
        ) : (
          <>
            {isError && (
              <ErrorWrapper id={LOGIN_ERROR_ID} role="alert">
                <Alert
                  icon={
                    <TriangleAlert
                      size={24}
                      color="var(--chakra-colors-red-solid)"
                    />
                  }
                >
                  {t('loginPrompt.invalidCredentials')}
                </Alert>
              </ErrorWrapper>
            )}
            {/* Field errors are both live alerts and aria-describedby targets.
                Some screen readers repeat the message when focus moves to the
                field, but dropping either side loses the announcement when
                focus is already on the field or when the field is revisited. */}
            {errUsername !== '' && (
              <ErrorWrapper id={USERNAME_ERROR_ID} role="alert">
                <Text
                  fontStyle={FontStyle.bSmall}
                  text={errUsername}
                  color="var(--chakra-colors-red-solid)"
                />
              </ErrorWrapper>
            )}
            {errPassword !== '' && (
              <ErrorWrapper id={PASSWORD_ERROR_ID} role="alert">
                <Text
                  fontStyle={FontStyle.bSmall}
                  text={errPassword}
                  color="var(--chakra-colors-red-solid)"
                />
              </ErrorWrapper>
            )}
            <form>
              <InputWrapper>
                <Input
                  id="username-input"
                  placeholder={t('loginPrompt.usernamePlaceholder')}
                  type="email"
                  name="email"
                  autoComplete="username"
                  error={isUsernameInvalid}
                  aria-describedby={describedBy(
                    isError && LOGIN_ERROR_ID,
                    errUsername !== '' && USERNAME_ERROR_ID,
                  )}
                  ref={usernameInput}
                  value={username}
                  onChange={onChangeUsername}
                  onKeyDown={handleKeydownEvent('username')}
                />
              </InputWrapper>
              <InputWrapper>
                <Input
                  id="password-input"
                  placeholder={t('loginPrompt.passwordPlaceholder')}
                  name="password"
                  autoComplete="current-password"
                  error={isPasswordInvalid}
                  aria-describedby={describedBy(
                    isError && LOGIN_ERROR_ID,
                    errPassword !== '' && PASSWORD_ERROR_ID,
                  )}
                  ref={passwordInput}
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={onChangePassword}
                  onKeyDown={handleKeydownEvent('password')}
                />
                <PassButtonWrapper>
                  <PasswordVisibilityButton
                    type="button"
                    aria-label={t('loginPrompt.showPassword')}
                    aria-pressed={showPassword}
                    aria-controls="password-input"
                    onClick={togglePasswordObfuscate}
                  >
                    {showPassword ? (
                      <EyeOff
                        size={20}
                        color="var(--chakra-colors-fg-muted)"
                        aria-hidden
                      />
                    ) : (
                      <Eye
                        size={20}
                        color="var(--chakra-colors-fg-muted)"
                        aria-hidden
                      />
                    )}
                  </PasswordVisibilityButton>
                </PassButtonWrapper>
              </InputWrapper>
              <LoginButtonWrapper>
                <MangoButton
                  onClick={handleLogin}
                  disabled={disabled || isFetching}
                  size={Size.md}
                >
                  {t('loginPrompt.logIn')}
                </MangoButton>
              </LoginButtonWrapper>
            </form>
            <ForgotButtonWrapper>
              <ForgotPasswordLink
                as={Link}
                to={ApplicationRoutes.FORGOT_PASSWORD}
                onClick={handleForgotPassword}
              >
                <Text
                  fontStyle={FontStyle.bSmall}
                  text={t('loginPrompt.forgotPassword')}
                  color="var(--chakra-colors-brand-solid)"
                />
              </ForgotPasswordLink>
            </ForgotButtonWrapper>
          </>
        )}
        <LoginEnvironmentToggle />
      </ContentWrapper>
    </>
  )
}
